import React from "react";
import { useNavigate } from "react-router";
import { CALENDAR_SALE } from "../../../routes";
import { goToPreviousPage } from "../../../../utils/goToPreviousPage";
import "./style/calendarFlow.scss";
import CalendarDayPicker from "../DayPicker";
import { Exercise } from "../../../models/Exercise";
import CalendarOneExercise from "./CalendarOneExercise";
import CalendarMultiExercise from "./CalendarMultiExercise";
import { SelectionState } from "./hooks/useCalendarModal";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";
import FlowPanel from "./shared/FlowPanel";

interface CalendarFlowProps {
  selectedDate: Date;
  exerciseName: string;
  listaDat?: Date[];
  soldOutDates?: Date[];
  daysLoaded: boolean;
  exercise: Exercise[];
  alerts: string;
  selectionState: SelectionState;
  onDateSelect: (date: Date, name: string) => void;
  onTimeSelect?: () => void;
  joinToExerciseByPass: (exerciseIds: string, assignType: boolean, passId: number) => void;
  refreshCapacityAfterAddTicket: () => void;
  showHoursToChoose: boolean;
  hoursLoading: boolean;
}

/**
 * Ścieżka zakupu: data → godzina → bilety (albo data → bilety) w jednej siatce o równej wysokości kolumn.
 * Układ jest responsywny wyłącznie w CSS (calendarFlow.scss), bez osobnego widoku mobilnego.
 */
const CalendarFlow: React.FC<CalendarFlowProps> = ({
  selectedDate,
  exerciseName,
  listaDat = [],
  soldOutDates = [],
  daysLoaded,
  exercise,
  alerts,
  selectionState,
  onDateSelect,
  onTimeSelect,
  joinToExerciseByPass,
  refreshCapacityAfterAddTicket,
  showHoursToChoose,
  hoursLoading,
}) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const isDateSelected = selectionState >= SelectionState.DATE_SELECTED;
  const threeSteps = showHoursToChoose !== false;

  const dateLabel = isDateSelected ? format(selectedDate, "EEE, d MMMM yyyy", { locale }) : "";
  const shortDate = isDateSelected ? format(selectedDate, "d MMM", { locale }) : "";

  const navigate = useNavigate();

  // „Kup kolejne bilety": wracamy do głównego okna sklepu (tam, skąd użytkownik wszedł w ofertę)
  const handleBuyMore = () => {
    goToPreviousPage(navigate, CALENDAR_SALE);
    window.scrollTo({ top: 0 });
  };

  const ticketsStep = threeSteps ? 3 : 2;
  const lockedTickets = (message: string) => (
    <FlowPanel
      step={ticketsStep}
      title={t("wybierz_rodzaj_i_ilosc_biletow")}
      locked
      lockedMessage={message}
      className="flow-col--tickets"
    />
  );

  const renderLaterColumns = () => {
    if (!isDateSelected) {
      return threeSteps ? (
        <>
          <FlowPanel
            step={2}
            title={t("godzina_wejscia")}
            locked
            lockedMessage={t("kalendarz_wybierz_date_zeby_zobaczyc_godziny")}
            className="flow-col--hours"
          />
          {lockedTickets(t("kalendarz_wybierz_date_i_godzine"))}
        </>
      ) : (
        lockedTickets(t("kalendarz_wybierz_date_zeby_zobaczyc_bilety"))
      );
    }

    if (hoursLoading) {
      return (
        <>
          <FlowPanel
            step={2}
            title={threeSteps ? t("godzina_wejscia") : t("wybierz_rodzaj_i_ilosc_biletow")}
            className={threeSteps ? "flow-col--hours" : "flow-col--tickets"}
          >
            <EASkeletonLoader height="200px" />
          </FlowPanel>
          {threeSteps && lockedTickets(t("wybierz_godzine"))}
        </>
      );
    }

    if (exercise.length === 0) {
      return (
        <>
          <FlowPanel
            step={2}
            title={threeSteps ? t("godzina_wejscia") : t("wybierz_rodzaj_i_ilosc_biletow")}
            className={threeSteps ? "flow-col--hours" : "flow-col--tickets"}
          >
            <div className="no-available-exercises">{t("Brak dostępnych godzin")}</div>
          </FlowPanel>
          {threeSteps && lockedTickets(t("wybierz_godzine"))}
        </>
      );
    }

    if (!threeSteps) {
      return (
        <CalendarOneExercise
          alertCalendar={alerts}
          joinToExerciseByPass={joinToExerciseByPass}
          exercise={exercise[0]}
          refreshCapacityAfterAddTicket={refreshCapacityAfterAddTicket}
          dateLabel={dateLabel}
          onBuyMore={handleBuyMore}
        />
      );
    }

    return (
      <CalendarMultiExercise
        alertCalendar={alerts}
        joinToExerciseByPass={joinToExerciseByPass}
        exercise={exercise}
        refreshCapacityAfterAddTicket={refreshCapacityAfterAddTicket}
        onTimeSelect={onTimeSelect}
        dateLabel={dateLabel}
        onBuyMore={handleBuyMore}
      />
    );
  };

  return (
    <div className={`calendar-flow ${threeSteps ? "calendar-flow--3" : "calendar-flow--2"}`}>
      <FlowPanel step={1} title={t("wybierz_date")} value={shortDate} done={isDateSelected} className="flow-col--date">
        <CalendarDayPicker
          getExercisesByDate={onDateSelect}
          selectedDate={selectedDate}
          exerciseName={exerciseName}
          daysLoaded={daysLoaded}
          hasSelection={isDateSelected}
          listaDat={listaDat}
          soldOutDates={soldOutDates}
        />
      </FlowPanel>

      {renderLaterColumns()}
    </div>
  );
};

export default CalendarFlow;
